import React, { useEffect } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../hooks/useAuth";
import { useHelpdeskNavigation } from "../hooks/useHelpdeskNavigation";
import { usePersistedFilters } from "../hooks/usePersistedFilters";
import { useSignalR } from "../contexts/SignalRContext";
import { Route } from "../routes/helpdesk/dashboard";
import {
  useAllOrganizationsDashboard,
  transformTicketTrendsForChart,
  transformActivitiesForDisplay,
} from "../hooks/useReports";
import { queryKeys } from "../lib/queryKeys";
import { MultiSelectFilter } from "../components/MultiSelectFilter";
import { CategoryReportCard } from "../components/CategoryReportCard";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { formatNumber, cn } from "@/lib/utils";
import { formatHoursDuration } from "../lib/dateUtils";
import { getActivityIconByType } from "@/lib/activityIcons";
import { DashboardSkeleton, DashboardDataSkeleton } from "../components/LoadingSkeleton";
import {
  Ticket,
  CheckCircle2,
  Users,
  Activity,
  ArrowRight,
  BarChart3,
  Clock,
  AlertTriangle,
  CircleDot,
  Hourglass,
  AlertCircle,
  PauseCircle,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

interface ActivityItem {
  id: string;
  type: string;
  action: string;
  description: string;
  timestamp: string;
  user?: string;
  ticketId?: string;
  ticketNumber?: number;
  ticketSubject?: string;
  organizationId?: string;
  organizationName?: string;
}

export const Dashboard: React.FC = () => {
  const { user, availableOrganizations } = useAuth();
  const { getPath } = useHelpdeskNavigation();
  const navigate = useNavigate();
  const searchParams = Route.useSearch();
  const { signalR, isConnected } = useSignalR();
  const queryClient = useQueryClient();

  // Default filter values
  const defaultFilterValues = {
    organizationIds: [] as string[],
  };

  // Initialize filter persistence (global across all orgs)
  const storageKey = `helpdesk.dashboard.filters`;
  const { updateFilters, isInitialized } = usePersistedFilters(
    storageKey,
    defaultFilterValues,
    searchParams
  );

  // Organization filter from URL search params (persistent via localStorage backup)
  const selectedOrgIds = searchParams.organizationIds ?? [];

  // Update URL and localStorage when filter changes
  const setSelectedOrgIds = (orgIds: string[]) => {
    updateFilters({ organizationIds: orgIds.length > 0 ? orgIds : [] });
  };

  const {
    data: dashboardData,
    isLoading: dashboardLoading,
    error: dashboardError,
  } = useAllOrganizationsDashboard(selectedOrgIds.length > 0 ? selectedOrgIds : undefined);

  // Navigation helper for clickable dashboard cards
  const navigateToTickets = (filters: {
    statuses?: number[];
    priorities?: number[];
    showOverdue?: boolean;
  }) => {
    navigate({
      to: getPath.tickets(),
      search: {
        statuses: filters.statuses || [],
        priorities: filters.priorities || [],
        showOverdue: filters.showOverdue,
        organizationIds: selectedOrgIds.length > 0 ? selectedOrgIds : undefined,
        page: 1,
        pageSize: 20,
        sortBy: 'createdat',
        sortDescending: true,
      },
    });
  };

  // Clickable card styles
  const clickableCardStyles = "cursor-pointer hover:shadow-lg hover:-translate-y-1 transition-all duration-300";

  // Process dashboard data
  const ticketTrendData = dashboardData
    ? transformTicketTrendsForChart(dashboardData.ticketTrends)
    : [];

  const recentActivities: ActivityItem[] = dashboardData
    ? transformActivitiesForDisplay(dashboardData.recentActivities)
    : [];

  const stats = dashboardData?.stats;

  // Calculate priority distribution from consolidated stats
  const priorityData = stats
    ? [
        { name: "Low", value: stats.lowPriorityTickets, color: "#10b981" },
        {
          name: "Medium",
          value: stats.mediumPriorityTickets,
          color: "#f59e0b",
        },
        { name: "High", value: stats.highPriorityTickets, color: "#ef4444" },
        {
          name: "Critical",
          value: stats.criticalPriorityTickets,
          color: "#dc2626",
        },
      ]
    : [
        { name: "Low", value: 0, color: "#10b981" },
        { name: "Medium", value: 0, color: "#f59e0b" },
        { name: "High", value: 0, color: "#ef4444" },
        { name: "Critical", value: 0, color: "#dc2626" },
      ];

  // Auto-select all organizations if none selected (only after filter restoration is complete)
  useEffect(() => {
    if (isInitialized && availableOrganizations.length > 0 && selectedOrgIds.length === 0) {
      setSelectedOrgIds(availableOrganizations.map(org => org.id));
    }
  }, [isInitialized, availableOrganizations, selectedOrgIds.length, setSelectedOrgIds]);

  // SignalR real-time updates
  useEffect(() => {
    if (!isConnected) return;

    const handleTicketCreated = () => {
      console.log('Dashboard: New ticket created');
      queryClient.invalidateQueries({
        queryKey: queryKeys.reports.all,
      });
    };

    const handleTicketUpdate = () => {
      console.log('Dashboard: Ticket updated');
      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.reports.all,
        });
      }, 100);
    };

    const handleTicketActivity = () => {
      console.log('Dashboard: Ticket activity');
      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.reports.all,
        });
      }, 100);
    };

    const handleCommentAdded = () => {
      console.log('Dashboard: Comment added');
      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.reports.all,
        });
      }, 100);
    };

    // Subscribe to events and store cleanup functions
    const cleanupTicketCreated = signalR.on('TicketCreated', handleTicketCreated);
    const cleanupTicketUpdate = signalR.on('TicketUpdate', handleTicketUpdate);
    const cleanupTicketUpdated = signalR.on('TicketUpdated', handleTicketUpdate);
    const cleanupTicketActivity = signalR.on('TicketActivity', handleTicketActivity);
    const cleanupCommentAdded = signalR.on('CommentAdded', handleCommentAdded);

    // Cleanup - only removes this component's handlers, not global handlers
    return () => {
      cleanupTicketCreated();
      cleanupTicketUpdate();
      cleanupTicketUpdated();
      cleanupTicketActivity();
      cleanupCommentAdded();
    };
  }, [isConnected, signalR, queryClient]);

  // Show full skeleton only if user isn't loaded yet
  if (!user) {
    return <DashboardSkeleton />;
  }

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Welcome Section - Always visible */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
            Welcome back, {user.firstName}!
          </h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
            Here's what's happening with your tickets today
          </p>
        </div>

        {/* Organization Filter - Always visible when user has multiple orgs */}
        {availableOrganizations.length > 1 && (
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-foreground whitespace-nowrap">
              Organization:
            </label>
            <div className="w-48 sm:w-56">
              <MultiSelectFilter
                label="Organization"
                options={availableOrganizations.map(org => ({ value: org.id, label: org.name }))}
                selectedValues={selectedOrgIds}
                onChange={setSelectedOrgIds}
                requireAtLeastOne={true}
                showSelectAllControls={true}
              />
            </div>
          </div>
        )}
      </div>

      {/* Data Section - Show skeleton when loading, otherwise show actual data */}
      {dashboardLoading ? (
        <DashboardDataSkeleton />
      ) : (
        <>
      {/* Metrics Cards */}
      <div className="space-y-3 md:space-y-4">
        {/* Ticket Status Flow */}
        <div>
          <h3 className="text-base sm:text-lg font-semibold mb-2 sm:mb-3">Ticket Status</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
            <Card
              className={`border-0 border-l-4 border-l-blue-500 ${clickableCardStyles}`}
              onClick={() => navigateToTickets({ statuses: [1] })}
              role="button"
              tabIndex={0}
            >
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Open</CardTitle>
                <CircleDot className="h-4 w-4 text-blue-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {dashboardLoading ? (
                    <div className="h-8 w-12 bg-muted animate-pulse rounded"></div>
                  ) : (
                    formatNumber(stats?.openTickets || 0)
                  )}
                </div>
                <p className="text-xs text-muted-foreground">New tickets</p>
              </CardContent>
            </Card>

            <Card
              className={`border-0 border-l-4 border-l-yellow-500 ${clickableCardStyles}`}
              onClick={() => navigateToTickets({ statuses: [2] })}
              role="button"
              tabIndex={0}
            >
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  In Progress
                </CardTitle>
                <Hourglass className="h-4 w-4 text-yellow-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {dashboardLoading ? (
                    <div className="h-8 w-12 bg-muted animate-pulse rounded"></div>
                  ) : (
                    formatNumber(stats?.inProgressTickets || 0)
                  )}
                </div>
                <p className="text-xs text-muted-foreground">Being worked on</p>
              </CardContent>
            </Card>

            <Card
              className={`border-0 border-l-4 border-l-orange-500 ${clickableCardStyles}`}
              onClick={() => navigateToTickets({ statuses: [3] })}
              role="button"
              tabIndex={0}
            >
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Pending</CardTitle>
                <AlertCircle className="h-4 w-4 text-orange-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {dashboardLoading ? (
                    <div className="h-8 w-12 bg-muted animate-pulse rounded"></div>
                  ) : (
                    formatNumber(stats?.pendingTickets || 0)
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Awaiting response
                </p>
              </CardContent>
            </Card>

            <Card
              className={`border-0 border-l-4 border-l-purple-500 ${clickableCardStyles}`}
              onClick={() => navigateToTickets({ statuses: [6] })}
              role="button"
              tabIndex={0}
            >
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">On Hold</CardTitle>
                <PauseCircle className="h-4 w-4 text-purple-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {dashboardLoading ? (
                    <div className="h-8 w-12 bg-muted animate-pulse rounded"></div>
                  ) : (
                    formatNumber(stats?.onHoldTickets || 0)
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Paused tickets
                </p>
              </CardContent>
            </Card>

            <Card
              className={`border-0 border-l-4 border-l-green-500 ${clickableCardStyles}`}
              onClick={() => navigateToTickets({ statuses: [4, 5] })}
              role="button"
              tabIndex={0}
            >
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Resolved</CardTitle>
                <CheckCircle2 className="h-4 w-4 text-green-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {dashboardLoading ? (
                    <div className="h-8 w-12 bg-muted animate-pulse rounded"></div>
                  ) : (
                    formatNumber(stats?.resolvedTickets || 0)
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Completed tickets
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Key Metrics */}
        <div>
          <h3 className="text-base sm:text-lg font-semibold mb-2 sm:mb-3">Key Metrics</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <Card
              className={`border-0 border-l-4 border-l-purple-500 ${clickableCardStyles}`}
              onClick={() => navigateToTickets({})}
              role="button"
              tabIndex={0}
            >
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Total Tickets
                </CardTitle>
                <Ticket className="h-4 w-4 text-purple-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {dashboardLoading ? (
                    <div className="h-8 w-12 bg-muted animate-pulse rounded"></div>
                  ) : (
                    formatNumber(stats?.totalTickets || 0)
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  All tickets in system
                </p>
              </CardContent>
            </Card>

            <Card
              className={`border-0 border-l-4 ${clickableCardStyles} ${
                !dashboardLoading && (stats?.overdueTickets || 0) > 0
                  ? "border-l-red-500 bg-red-50 dark:bg-red-950"
                  : "border-l-red-500"
              }`}
              onClick={() => navigateToTickets({ showOverdue: true })}
              role="button"
              tabIndex={0}
            >
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle
                  className={`text-sm font-medium ${
                    !dashboardLoading && (stats?.overdueTickets || 0) > 0
                      ? "text-red-700 dark:text-red-300"
                      : ""
                  }`}
                >
                  Overdue
                </CardTitle>
                <AlertTriangle className="h-4 w-4 text-red-500" />
              </CardHeader>
              <CardContent>
                <div
                  className={`text-2xl font-bold ${
                    !dashboardLoading && (stats?.overdueTickets || 0) > 0
                      ? "text-red-600 dark:text-red-400"
                      : ""
                  }`}
                >
                  {dashboardLoading ? (
                    <div className="h-8 w-12 bg-muted animate-pulse rounded"></div>
                  ) : (
                    formatNumber(stats?.overdueTickets || 0)
                  )}
                </div>
                <p
                  className={`text-xs ${
                    !dashboardLoading && (stats?.overdueTickets || 0) > 0
                      ? "text-red-600 dark:text-red-400"
                      : "text-muted-foreground"
                  }`}
                >
                  {!dashboardLoading && (stats?.overdueTickets || 0) > 0
                    ? "Require immediate attention"
                    : "No overdue tickets"}
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 border-l-4 border-l-cyan-500">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Avg Response Time
                </CardTitle>
                <Clock className="h-4 w-4 text-cyan-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {dashboardLoading ? (
                    <div className="h-8 w-12 bg-muted animate-pulse rounded"></div>
                  ) : stats?.averageFirstResponseHoursLast30Days !== undefined ? (
                    formatHoursDuration(stats.averageFirstResponseHoursLast30Days)
                  ) : (
                    "-"
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Last 30 days
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 border-l-4 border-l-green-500">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Avg Resolution Time
                </CardTitle>
                <Clock className="h-4 w-4 text-green-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {dashboardLoading ? (
                    <div className="h-8 w-12 bg-muted animate-pulse rounded"></div>
                  ) : stats?.averageResolutionHoursLast30Days !== undefined ? (
                    formatHoursDuration(stats.averageResolutionHoursLast30Days)
                  ) : (
                    "-"
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Last 30 days
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

      </div>

      {/* Error Display */}
      {dashboardError && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="text-red-700">
              <div>Failed to load dashboard data.</div>
              <div className="mt-2">Please try refreshing the page.</div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        {/* Ticket Trend Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base sm:text-lg">Ticket Volume Trend</CardTitle>
            <CardDescription className="text-xs sm:text-sm">
              Tickets created vs completed this week
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[250px] sm:h-[300px]">
              {dashboardLoading ? (
                <div className="h-full bg-muted animate-pulse rounded"></div>
              ) : ticketTrendData.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center">
                  <div className="text-muted-foreground mb-4">
                    <BarChart3 className="h-16 w-16 mx-auto opacity-50" />
                  </div>
                  <h3 className="text-lg font-medium text-foreground mb-2">
                    No data available
                  </h3>
                  <p className="text-muted-foreground">
                    Ticket trends will appear here when you have ticket
                    activity.
                  </p>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={ticketTrendData}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      className="stroke-gray-200 dark:stroke-gray-700"
                    />
                    <XAxis
                      dataKey="name"
                      className="text-xs"
                      tick={{ fill: "currentColor" }}
                    />
                    <YAxis
                      className="text-xs"
                      tick={{ fill: "currentColor" }}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "hsl(var(--popover))",
                        color: "hsl(var(--popover-foreground))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: "8px",
                        boxShadow:
                          "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                        fontSize: "14px",
                        fontWeight: "500",
                      }}
                      labelStyle={{
                        color: "hsl(var(--popover-foreground))",
                        fontWeight: "600",
                        marginBottom: "4px",
                      }}
                      itemStyle={{
                        color: "hsl(var(--popover-foreground))",
                        fontSize: "13px",
                      }}
                      separator={": "}
                      cursor={{
                        fill: "hsl(var(--muted) / 0.3)",
                        stroke: "none",
                      }}
                      formatter={(value: number) => formatNumber(value)}
                    />
                    <Legend
                      wrapperStyle={{
                        paddingTop: "16px",
                      }}
                    />
                    <Bar
                      dataKey="completed"
                      fill="#10b981"
                      name="Completed"
                      radius={[4, 4, 0, 0]}
                    />
                    <Bar
                      dataKey="created"
                      fill="hsl(var(--primary))"
                      name="Created"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Priority Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base sm:text-lg">Ticket Priority Distribution</CardTitle>
            <CardDescription className="text-xs sm:text-sm">Active tickets by priority level</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[250px] sm:h-[300px]">
              {dashboardLoading ? (
                <div className="h-full bg-muted/50 animate-pulse rounded"></div>
              ) : priorityData.every((item) => item.value === 0) ? (
                <div className="h-full flex flex-col items-center justify-center text-center">
                  <div className="text-muted-foreground mb-4">
                    <BarChart3 className="h-16 w-16 mx-auto opacity-50" />
                  </div>
                  <h3 className="text-lg font-medium text-foreground mb-2">
                    No active tickets
                  </h3>
                  <p className="text-muted-foreground">
                    Priority distribution will appear here when you have active
                    tickets.
                  </p>
                </div>
              ) : (
                <>
                  <div className="h-[180px] sm:h-[200px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={priorityData}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={80}
                          paddingAngle={5}
                          dataKey="value"
                          style={{ cursor: 'pointer' }}
                        >
                          {priorityData.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={entry.color}
                              onClick={() => navigateToTickets({ priorities: [index + 1] })}
                              style={{ cursor: 'pointer' }}
                            />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value: number) => formatNumber(value)} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-3 sm:mt-4">
                    {priorityData.map((item, index) => (
                      <div
                        key={item.name}
                        className="flex items-center gap-2 cursor-pointer hover:bg-muted/50 rounded px-1 py-0.5 transition-colors"
                        onClick={() => navigateToTickets({ priorities: [index + 1] })}
                        role="button"
                        tabIndex={0}
                      >
                        <div
                          className="w-3 h-3 rounded-full flex-shrink-0"
                          style={{ backgroundColor: item.color }}
                        />
                        <span className="text-sm text-muted-foreground truncate">
                          {item.name} ({formatNumber(item.value)})
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tickets by category - categories belong to one organization, so it needs exactly one selected */}
      <CategoryReportCard organizationId={selectedOrgIds.length === 1 ? selectedOrgIds[0] : null} />

      {/* Recent Activity and Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 items-start">
        {/* Recent Activity */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base sm:text-lg">Recent Activity</CardTitle>
                <CardDescription className="text-xs sm:text-sm">Latest updates from your team</CardDescription>
              </div>
              <Activity className="h-4 w-4 text-muted-foreground" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="max-h-[300px] sm:max-h-[400px] overflow-y-auto space-y-3 sm:space-y-4">
              {recentActivities.length > 0 ? (
                recentActivities.map((activity) => {
                  const { icon: Icon, color, bgColor } = getActivityIconByType(activity.type);
                  const ActivityContent = (
                    <div
                      key={activity.id}
                      className="flex items-start gap-3 group hover:bg-muted/50 p-2 rounded-lg transition-colors"
                    >
                      <div className={cn('p-1.5 rounded-md shrink-0', bgColor)}>
                        <Icon className={cn('h-3.5 w-3.5', color)} />
                      </div>
                      <div className="flex-1 space-y-1 min-w-0">
                        <p className="text-sm text-foreground">
                          {activity.description}
                          {activity.ticketNumber && (
                            <span className="text-blue-600 font-medium ml-1">
                              #{activity.ticketNumber}
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {activity.organizationName && (
                            <span className="font-medium text-foreground">{activity.organizationName}</span>
                          )}
                          {activity.organizationName && " • "}
                          {activity.timestamp} • {activity.user}
                        </p>
                      </div>
                    </div>
                  );

                  return activity.ticketId ? (
                    <Link
                      key={activity.id}
                      to={getPath.ticket(activity.ticketId)}
                      className="block cursor-pointer"
                    >
                      {ActivityContent}
                    </Link>
                  ) : (
                    ActivityContent
                  );
                })
              ) : (
                <div className="text-center py-8">
                  <Activity className="mx-auto h-8 w-8 text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">
                    No recent activity
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Activity feed will appear here
                  </p>
                </div>
              )}
            </div>
            <Link to={getPath.tickets()} className="block mt-4">
              <Button variant="outline" className="w-full">
                View All Activity
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base sm:text-lg">Quick Actions</CardTitle>
            <CardDescription className="text-xs sm:text-sm">Common tasks and shortcuts</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 sm:space-y-4">
            <Link to={getPath.newTicket()} className="block">
              <Button className="w-full justify-start">
                <Ticket className="mr-2 h-4 w-4" />
                Create New Ticket
              </Button>
            </Link>
            <Link to={getPath.tickets()} className="block">
              <Button variant="outline" className="w-full justify-start">
                <BarChart3 className="mr-2 h-4 w-4" />
                View All Tickets
              </Button>
            </Link>
            <Link to={getPath.customers()} className="block">
              <Button variant="outline" className="w-full justify-start">
                <Users className="mr-2 h-4 w-4" />
                Manage Customers
              </Button>
            </Link>

          </CardContent>
        </Card>
      </div>
        </>
      )}
    </div>
  );
};
